/**
 * Facettes de recherche → opérateurs Gmail.
 *
 * La chaîne de requête reste la SOURCE DE VÉRITÉ : les facettes et les jetons
 * affichés s'en déduisent. L'inverse (un état de facettes qui produirait la
 * requête) casserait dès que l'utilisateur tape un opérateur à la main, et
 * taper `from:` à la main est précisément l'usage qu'on veut garder.
 */

export interface Facet {
  label: string;
  /** Opérateur Gmail, ou `null` pour « pas de filtre » (efface le groupe). */
  op: string | null;
}

export interface FacetGroup {
  title: string;
  /** Un seul actif à la fois — les options se remplacent (portée, période). */
  exclusive: boolean;
  /** Préfixes que ce groupe possède, pour retirer l'ancien à la sélection. */
  owns: string[];
  facets: Facet[];
}

export const FACET_GROUPS: FacetGroup[] = [
  {
    title: "Portée",
    exclusive: true,
    owns: ["in:"],
    facets: [
      { label: "Boîte", op: "in:inbox" },
      { label: "Tous les mails", op: null },
      { label: "Envoyés", op: "in:sent" },
      { label: "Corbeille", op: "in:trash" },
    ],
  },
  {
    title: "Période",
    exclusive: true,
    owns: ["newer_than:"],
    facets: [
      { label: "7 jours", op: "newer_than:7d" },
      { label: "30 jours", op: "newer_than:30d" },
      { label: "Cette année", op: "newer_than:1y" },
      { label: "Tout", op: null },
    ],
  },
  {
    title: "Attributs",
    exclusive: false,
    owns: [],
    facets: [
      // `is:tracked` et « sans réponse » du handoff n'existent pas côté
      // Gmail et les ouvertures suivies ne sont pas indexées par fil : on
      // s'en tient aux opérateurs qui répondent vraiment.
      { label: "Pièce jointe", op: "has:attachment" },
      { label: "Non lu", op: "is:unread" },
      { label: "Suivi ★", op: "is:starred" },
      { label: "Important", op: "is:important" },
    ],
  },
];

/** Découpe une requête en jetons, en gardant les `"citations groupées"`. */
export function tokenize(query: string): string[] {
  return query.match(/"[^"]*"|\S+/g) ?? [];
}

export function hasToken(query: string, token: string): boolean {
  return tokenize(query).some((t) => t.toLowerCase() === token.toLowerCase());
}

export function removeToken(query: string, token: string): string {
  return tokenize(query)
    .filter((t) => t.toLowerCase() !== token.toLowerCase())
    .join(" ");
}

/** Retire tous les jetons commençant par l'un des préfixes donnés. */
export function removePrefixes(query: string, prefixes: string[]): string {
  if (prefixes.length === 0) return query;
  return tokenize(query)
    .filter((t) => !prefixes.some((p) => t.toLowerCase().startsWith(p)))
    .join(" ");
}

export function addToken(query: string, token: string): string {
  const base = query.trim();
  return base.length > 0 ? `${base} ${token}` : token;
}

/** Applique une facette : bascule, ou remplace au sein d'un groupe exclusif. */
export function applyFacet(query: string, group: FacetGroup, facet: Facet): string {
  if (facet.op && hasToken(query, facet.op)) return removeToken(query, facet.op);
  let next = group.exclusive ? removePrefixes(query, group.owns) : query;
  if (facet.op) next = addToken(next, facet.op);
  return next.trim();
}
